import Input from "@Components/Input";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useClickOutside from "hooks/useOutsideClick";
import { MouseEvent, useEffect, useRef, useState, useMemo } from "react";
import styles from "./university.module.scss";
import SearchIcon from "@Icons/SearchIcon";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import { AppState } from "@Store/index";
import { getLocation } from "@Pages/PropertyPage/data/helper";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import fetchUniversityData from "@Pages/Search/components/FilterSection/data/fetchUniversityData";
import { setFilterUniversityList } from "@Pages/Search/data/searchPageReducer";
import RadioChip from "../../../FilterSubsection/components/RadioChip";
import config from "@Config/index";
import debounce from "helpers/debounce";
import useWhitelabel from "hooks/useWhitelabel";
import useNoindexSlugs from "hooks/useNoindexSlugs";

type Option = { label: string; value: string };

const University = ({ value = "", dispatch, onApplyClick }: any) => {
  const { region, universityList, quickUniversityLinks, userLanguage } = useSelector(
    ({ searchPageData, appData }: AppState) => ({
      region: searchPageData.region,
      universityList: searchPageData.filterUniversityList,
      quickUniversityLinks: searchPageData?.region?.quick_university_links,
      userLanguage: appData?.userLanguage,
    }),
    shallowEqual,
  );
  const { data = [], isLoading } = universityList || {};
  const [isLoadingLocal, setIsLoadingLocal] = useState(false);
  const { t } = useTranslationHook();
  const noindexSlugs = useNoindexSlugs();
  const searchListDispatch = useDispatch();
  const [showOptions, setShowOptions] = useState(false);
  const universityName =
    data?.find((uni: any) => uni?.value === value)?.label ||
    quickUniversityLinks?.find((link: any) => link?.canonical_name === value)?.name ||
    "";
  const quickUniversityLinksAvailable = quickUniversityLinks?.length > 0;
  const [activeUniversity, setActiveUniversity] = useState(universityName || "");
  const [options, setOptions] = useState<Option[]>(data || []);
  const [selectedQuickUniversityLink, setSelectedQuickUniversityLink] = useState(
    universityName || "",
  );
  const ancestorId2 =
    region?.region_type === "establishment" || region?.region_type === "sublocality"
      ? region?.parent_id
      : region?.id;

  useEffect(() => {
    setActiveUniversity(universityName);
    setOptions(data);
  }, [JSON.stringify(data)]);

  const { isWhitelabel, partner_id } = useWhitelabel();

  const getUniversitiesOnSearchName = async (searchTerm: string) => {
    try {
      const searchedData = await fetchUniversityData({
        ancestorId: ancestorId2,
        regionType: "establishment",
        searchTerm,
        sortKey: "distance",
        isWhitelabel,
        partner_id,
        lang: userLanguage,
      });
      setOptions(searchedData);
    } catch (error) {
      console.error("Error fetching university data:", error);
    } finally {
      setIsLoadingLocal(false);
    }
  };

  const delayedSearch = useMemo(() => debounce(getUniversitiesOnSearchName, 300), [300]);

  useEffect(() => {
    if (showOptions && data.length === 0) {
      const ancestorId =
        region?.region_type === "establishment" || region?.region_type === "sublocality"
          ? region?.parent_id
          : region?.id;
      const getData = async () => {
        const unidata = await fetchUniversityData({
          ancestorId,
          isWhitelabel,
          partner_id,
          lang: userLanguage,
        });
        const finalData = {
          data: unidata,
          ancestorId,
          isLoading: false,
        };
        searchListDispatch(setFilterUniversityList(finalData));
      };
      getData();
    }
  }, [showOptions]);

  // Refetch university data when region changes (ancestorId changes)
  useEffect(() => {
    if (universityList?.ancestorId && ancestorId2 && universityList.ancestorId !== ancestorId2) {
      const getData = async () => {
        const unidata = await fetchUniversityData({
          ancestorId: ancestorId2,
          isWhitelabel,
          partner_id,
          lang: userLanguage,
        });
        const finalData = {
          data: unidata,
          ancestorId: ancestorId2,
          isLoading: false,
        };
        searchListDispatch(setFilterUniversityList(finalData));
      };
      getData();
    }
  }, [ancestorId2]);

  const optionsContainer = useRef<HTMLDivElement>(null);

  const handleOnClick = (e: MouseEvent<HTMLButtonElement>, option: Option) => {
    e.stopPropagation();
    setActiveUniversity(option.label);
    if (value !== option.value) {
      dispatch({ type: "university", payload: option.value });
      if (typeof onApplyClick === "function") {
        onApplyClick({ university: option.value });
      }

      if (option?.value) {
        let shouldSetSelectedQuickUniversityLinkEmpty = true;
        quickUniversityLinks?.forEach((link: any) => {
          if (link?.canonical_name === option?.value) {
            setSelectedQuickUniversityLink(option?.label);
            shouldSetSelectedQuickUniversityLinkEmpty = false;
          }
        });

        if (shouldSetSelectedQuickUniversityLinkEmpty) setSelectedQuickUniversityLink("");
      }
    }

    setShowOptions(false);
    gtagEventEmitter({
      action: "SP_filter_subsection_filter2_value_selected",
      category: "SP_filter_subsection2",
      value: "University",
    });
    try {
      localStorage.setItem(
        "filtered_university_and_region",
        `${option.label} - ${getLocation(region.location)}`,
      );
    } catch (event: any) {
      console.log(event);
    }
  };

  const handleChange = (str: string) => {
    if (str === "") {
      dispatch({ type: "university", payload: "" });
      setSelectedQuickUniversityLink("");
    }
    setActiveUniversity(str);
    const updatedOptions = data.filter((option: Option) =>
      option?.label?.toLowerCase()?.includes(str?.toLowerCase()),
    );

    if (updatedOptions.length !== 0) setOptions(updatedOptions);

    if (updatedOptions.length <= 1 && activeUniversity !== "") {
      delayedSearch(str);
    }
  };

  useEffect(() => {
    const selectedUniversity = data?.find((uni: any) => uni?.value === value)?.label || "";
    if (
      (selectedQuickUniversityLink?.length < 0 || value?.length <= 0) &&
      selectedUniversity !== activeUniversity
    ) {
      handleChange("");
    }
  }, [value]);

  useClickOutside(optionsContainer, () => setShowOptions(false));

  const handleQuickUniversityLinkClick = (name: string, canonical_name: string) => {
    setSelectedQuickUniversityLink(name);
    setActiveUniversity(name);
    if (value !== canonical_name) {
      dispatch({ type: "university", payload: canonical_name });
      if (typeof onApplyClick === "function") {
        onApplyClick({ university: canonical_name });
      }
    }
    gtagEventEmitter({
      action: "SP_filter_subsection_filter2_quick_link_selected",
      category: "SP_filter_subsection2",
      value: "University",
    });
    try {
      localStorage.setItem(
        "filtered_university_and_region",
        `${name} - ${getLocation(region.location)}`,
      );
    } catch (event: any) {
      console.error(event);
    }
  };

  const generateAmberUrl = (canonical_name: string) =>
    `${config.WEB_SERVICE_URL}places/search/${canonical_name}`;

  return (
    <div ref={optionsContainer}>
      <Input
        label=""
        leftIcon={<SearchIcon fill="#868e96" />}
        leftIconClassName={formatClassNames(styles.icon, showOptions && styles.focused)}
        rightIconClassName={formatClassNames(styles.closeIcon, showOptions && styles.focused)}
        clearable={activeUniversity}
        value={activeUniversity}
        type="text"
        placeholder={t("searchpage.components.filtersection.universityplaceholder")}
        showPlaceholder
        onChange={handleChange}
        handleClear={() => handleChange("")}
        onFocus={() => setShowOptions(true)}
        inputClassName={styles.uniInput}
        dataTestId="search-university-filter-input"
        containerClassName={styles.inputContainer}
      />
      {quickUniversityLinksAvailable && !showOptions && (
        <div className={formatClassNames(styles.quickLinksContainer)}>
          {quickUniversityLinks?.map(({ name, canonical_name }: any) => (
            <RadioChip
              name={name}
              label={t(name)}
              value={generateAmberUrl(canonical_name)}
              isSelected={selectedQuickUniversityLink === name}
              key={generateAmberUrl(canonical_name)}
              setSelectedValue={() => handleQuickUniversityLinkClick(name, canonical_name)}
              asLink
              hideHrefUntilMounted={noindexSlugs.has(canonical_name)}
            />
          ))}
        </div>
      )}
      {showOptions && options.length > 0 && (
        <div className={styles.options_container}>
          {options.map((option: Option) => (
            <button
              type="button"
              className={formatClassNames(styles.option)}
              key={option.value}
              onClick={(e) => handleOnClick(e, option)}
              data-testid="university-filter-search-result"
            >
              <div style={{ display: "flex", gap: "8px", whiteSpace: "break-spaces" }}>
                {/* <RadioButton isSelected={isSelected} /> */}
                {option.label}
              </div>
            </button>
          ))}
        </div>
      )}

      {showOptions && (isLoading || isLoadingLocal) && (
        <div className={styles.options_info_container} data-testid="filter-loading">
          Loading...
        </div>
      )}

      {options?.length === 0 && !isLoading && !isLoadingLocal && (
        <div className={styles.options_info_container} data-testid="no-universities-found-text">
          No Universities Found
        </div>
      )}
    </div>
  );
};

export default wrapperHOC(University, {
  componentName: "University-FilterSubsection2-SearchListDesktop-SearchPage",
  showForChina: true,
});
